import React, { useRef, useEffect, useState, useCallback } from 'react';
import Globe from 'react-globe.gl';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';

// SVG Logo Component
const AdtiguusLogo = ({ variant = 'white', className = '' }) => {
  const svgPath = variant === 'black' 
    ? '/ADTIGUUS_Black Text.svg' 
    : '/ADTIGUUS_White Text.svg';
  
  return (
    <img 
      src={svgPath} 
      alt="ADTIGUUS" 
      className={`adtiguus-logo ${className}`}
      style={{
        height: '60px',
        width: 'auto',
        objectFit: 'contain'
      }}
    />
  );
};

function App() {
  const globeEl = useRef();
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [signupUsername, setSignupUsername] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Get correct altitude based on screen size
  const getMobileAltitude = useCallback(() => {
    const width = window.innerWidth;
    if (width <= 380) {
      return isLoggedIn ? 3.5 : 5.0;
    } else if (width <= 480) {
      return isLoggedIn ? 3.0 : 4.5;
    } else if (width <= 768) {
      return isLoggedIn ? 2.5 : 4.0;
    }
    return isLoggedIn ? 1.5 : 2.5;
  }, [isLoggedIn]);

  useEffect(() => {
    // Make sure globe exists before configuring
    const globe = globeEl.current;
    if (globe) {
      // Short timeout to ensure the component is fully mounted
      setTimeout(() => {
        globe.controls().autoRotate = true;
        globe.controls().autoRotateSpeed = 0.5;
        globe.controls().enableZoom = true;
        globe.controls().enableRotate = true;
        
        // Set initial camera position based on login state
        const altitude = getMobileAltitude();
        
        if (isLoggedIn) {
          // Zoom to USA if logged in
          globe.pointOfView({
            lat: 39.6,
            lng: -98.5,
            altitude: altitude
          });
        } else {
          // Far view if not logged in
          globe.pointOfView({ altitude: altitude });
        }
      }, 100);
    }
  }, [isLoggedIn, isMobile, getMobileAltitude]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      
      // Update globe camera on resize
      if (globeEl.current) {
        const altitude = getMobileAltitude();
        globeEl.current.pointOfView({ altitude });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getMobileAltitude]);

  const handleGlobeClick = () => {
    if (!showLogin && !showSignup && !isLoggedIn) {
      setShowLogin(true);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    // Non-functional as requested - button click does nothing
    console.log('Login button clicked - non-functional as requested');
    // Here we would normally make an API call to validate credentials
    // For now, just simulate a successful login with any credentials
    // if (username && password) {
    //   setIsLoggedIn(true);
    //   setShowLogin(false);
    //   
    //   // Update camera position after login
    //   setTimeout(() => {
    //     if (globeEl.current) {
    //       const altitude = getMobileAltitude();
    //       globeEl.current.pointOfView({
    //         lat: 39.6,
    //         lng: -98.5,
    //         altitude: altitude
    //       });
    //     }
    //   }, 100);
    // }
  };

  const handleSignupClick = () => {
    // Non-functional as requested - button click does nothing
    console.log('Signup link clicked - non-functional as requested');
    // setShowLogin(false);
    // setShowSignup(true);
  };

  const handleBackToLogin = () => {
    // Non-functional as requested - button click does nothing  
    console.log('Back to Login clicked - non-functional as requested');
    // setShowSignup(false);
    // setShowLogin(true);
  };

  const handleSignup = (e) => {
    e.preventDefault();
    // Non-functional as requested - button click does nothing
    console.log('Create Account button clicked - non-functional as requested');
    // Here we would normally make an API call to register the user
    // For now, just simulate a successful signup
    // if (signupUsername && signupEmail && signupPassword && signupPassword === signupConfirmPassword) {
    //   // Set the username from signup to use in login
    //   setUsername(signupUsername);
    //   setPassword(signupPassword);
    //   
    //   // Switch back to login form
    //   setShowSignup(false);
    //   setShowLogin(true);
    //   
    //   // Clear signup form
    //   setSignupUsername('');
    //   setSignupEmail('');
    //   setSignupPassword('');
    //   setSignupConfirmPassword('');
    // }
  };

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      <div className="starry-background">
        <div className="stars"></div>
      </div>
      
      {/* Main Globe */}
      <div 
        className={`globe-wrapper ${isMobile ? 'mobile' : 'desktop'}`}
        onClick={handleGlobeClick}
      >
        <Globe
          ref={globeEl}
          globeImageUrl="https://raw.githubusercontent.com/vasturiano/three-globe/master/example/img/earth-blue-marble.jpg"
          backgroundColor="rgba(0,0,0,0)"
          atmosphereColor="rgba(65,105,225,0.7)"
          atmosphereAltitude={0.25}
          width={isMobile ? window.innerWidth : window.innerWidth}
          height={isMobile ? window.innerHeight : window.innerHeight}
        />
      </div>
      
      {/* Login Page (Second Load Page) */}
      {showLogin && !isLoggedIn && !showSignup && (
        <div className="login-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="login-container">
            <div className="logo-text">
              <AdtiguusLogo />
            </div>
            
            <form className="login-form-container" onSubmit={handleLogin}>
              <input 
                type="text" 
                className="login-input" 
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    document.querySelector('input[type="password"]')?.focus();
                  }
                }}
                autoFocus
              />
              
              <input 
                type="password" 
                className="login-input" 
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && username && password) {
                    handleLogin(e);
                  }
                }}
              />
              
              <button 
                type="submit" 
                className="login-button" 
                disabled={!username || !password}
              >
                Log In
              </button>
            </form>
            
            <div className="signup-link" onClick={handleSignupClick}>Sign Up</div>
          </div>
        </div>
      )}
      
      {/* Sign Up Page */}
      {showSignup && !isLoggedIn && !showLogin && (
        <div className="login-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="login-container">
            <div className="logo-text">
              <AdtiguusLogo />
            </div>
            
            <form className="signup-form-container" onSubmit={handleSignup}>
              <input 
                type="text" 
                className="login-input" 
                placeholder="Username"
                value={signupUsername}
                onChange={(e) => setSignupUsername(e.target.value)}
                autoFocus
              />
              
              <input 
                type="email" 
                className="login-input" 
                placeholder="Email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
              />
              
              <input 
                type="password" 
                className="login-input" 
                placeholder="Password"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
              />
              
              <input 
                type="password" 
                className="login-input" 
                placeholder="Confirm Password"
                value={signupConfirmPassword}
                onChange={(e) => setSignupConfirmPassword(e.target.value)}
              />
              
              <button 
                type="submit" 
                className="login-button" 
                disabled={!signupUsername || !signupEmail || !signupPassword || signupPassword !== signupConfirmPassword}
              >
                Create Account
              </button>
            </form>
            
            <div className="signup-link" onClick={handleBackToLogin}>Back to Login</div>
          </div>
        </div>
      )}
      
      {/* User Profile (After Login) */}
      {isLoggedIn && (
        <div className={`user-profile ${isMobile ? 'mobile' : 'desktop'}`}>
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-image-container">
                <img 
                  src="https://i.pravatar.cc/150?img=5" 
                  alt="Profile" 
                  className="profile-image" 
                />
              </div>
              <div className="profile-username">{username || 'julia roberts'}</div>
              <div className="online-status">●</div>
            </div>
            
            <div className="profile-stats">
              <div className="stat-item">
                <div className="stat-value">109</div>
                <div className="stat-label">posts</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">2,052</div>
                <div className="stat-label">followers</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">89</div>
                <div className="stat-label">following</div>
              </div>
            </div>
            
            <div className="profile-navigation">
              <div className="nav-dots">
                <span className="nav-dot active"></span>
                <span className="nav-dot"></span>
                <span className="nav-dot"></span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap the existing App component with AuthProvider at the bottom
const AppWithProviders = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default AppWithProviders;
